import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { LoadingContainer } from "go-core/components/Loading";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	ApplicationApi,
	ExternalProviderMenuApi,
	ExternalProviderMenuConfigApi,
	ExternalProviderMenuConfigOptionsApi,
	MenuProvider,
} from "../../../../../../../../../../../services/Api/Organization/types";
import MenuExternalPortalMenu from "./Menu/MenuExternalPortalMenu";
import MenuExternalPortalSettings from "./MenuExternalPortalSettings";

interface MatchParams {
	menuId: string;
}

interface Props {
	selectedGoOrder: ApplicationApi | undefined;
	provider: MenuProvider | undefined;
}

const MenuExternalPortalMenuPreview = ({ selectedGoOrder, provider }: Props) => {
	const [menuLoading, setMenuLoading] = useState(true);
	const [configLoading, setConfigLoading] = useState(true);
	const { addFlash } = useFlash();
	const [menu, setMenu] = useState<ExternalProviderMenuApi[]>();
	const [config, setConfig] = useState<ExternalProviderMenuConfigApi>();
	const [configOptions, setConfigOptions] = useState<ExternalProviderMenuConfigOptionsApi>();
	const { menuId } = useParams<MatchParams>();
	const appId = selectedGoOrder?.id;
	const [menuConfigParams, setMenuConfigParams] = useState<ExternalProviderMenuConfigApi | undefined>(undefined);
	const [forceFetchMenu, setForceFetchMenu] = useState(false);
	const [settingsVisible, setSettingsVisible] = useState(false);
	const { t } = useTranslation();

	useEffect(() => {
		handleInit();
	}, [provider]);

	useEffect(() => {
		if (forceFetchMenu) {
			fetchMenu(menuConfigParams);
			setForceFetchMenu(false);
		}
	}, [forceFetchMenu]);

	const handleInit = async () => {
		const configRes = await fetchConfig();
		await fetchMenu(!settingsVisible ? undefined : configRes);
	};

	const fetchConfig = async () => {
		if (!provider) return;

		setConfigLoading(true);
		try {
			const configRes = await apiOrganization.getApplicationMenuDefaultConfig(provider);
			const optionsRes = await apiOrganization.getApplicationMenuConfigOptions(provider);
			setConfigOptions(optionsRes);
			const parsedConfig = {
				...configRes,
				extra_mappers_form: (optionsRes.extra_mappers || []).map((option) => {
					const valueInConfig = (configRes.extra_mappers || []).find((mapper) => mapper === option);
					return {
						value: !!valueInConfig,
						name: option,
					};
				}),
			};
			setConfig(parsedConfig);
			setConfigLoading(false);
			return parsedConfig;
		} catch (err) {
			setConfigLoading(false);
			handleError.alert(err, addFlash);
		}
	};

	const handleFetchMenuErrors = (error: ApiError) => {
		let constraintTitle = "";
		const errorCode = error.code || "";
		const regex = /__(.*?)__(?!.)|__(.*?)$/g;
		let names = (errorCode.match(regex)?.map((match) => match.slice(2, -2)) || []).join().split("__");
		names = names.map((name) => name.replaceAll("_", " "));

		if (names.length === 2) {
			if (errorCode.startsWith("combine_variants_group_option_not_exist")) {
				constraintTitle = t("constraints.combine_variants_group_option_not_exist", {
					groupName: names[0],
					optionName: names[1],
				});
			}
			if (errorCode.startsWith("group_exists_in_flat_configuration")) {
				constraintTitle = t("constraints.group_exists_in_flat_configuration", {
					productName: names[0],
					groupName: names[1],
				});
			}
			if (errorCode.startsWith("options_not_exist_in_groups_but_permitted")) {
				constraintTitle = t("constraints.options_not_exist_in_groups_but_permitted", {
					groupName: names[0],
					productName: names[1],
				});
			}

			addFlash({
				msg: constraintTitle,
				type: "danger",
				messageAlreadyCreated: true,
			});
		}
	};

	const fetchMenu = async (configData?: ExternalProviderMenuConfigApi) => {
		if (!provider) return;

		setMenuLoading(true);
		try {
			const res = await apiOrganization.checkAppProviderMenu(
				{
					provider,
					menu_id: Number(menuId),
					config: !settingsVisible ? undefined : configData,
				},
				appId
			);
			setMenu(res);
		} catch (err) {
			const errors = handleException(err);
			const customError = errors.find(
				(error) =>
					error.code?.startsWith("options_not_exist_in_groups_but_permitted") ||
					error.code?.startsWith("group_exists_in_flat_configuration") ||
					error.code?.startsWith("combine_variants_group_option_not_exist")
			);
			if (customError) {
				handleFetchMenuErrors(customError);
			} else handleError.alert(err, addFlash);
		}
		setMenuLoading(false);
	};

	if (menuLoading && configLoading) return <LoadingContainer />;

	return provider ? (
		<>
			<MenuExternalPortalSettings
				config={config}
				configOptions={configOptions}
				setMenuConfigParams={setMenuConfigParams}
				setForceFetchMenu={setForceFetchMenu}
				setVisible={setSettingsVisible}
				visible={settingsVisible}
				provider={provider}
			/>
			{menuLoading ? <LoadingContainer /> : <MenuExternalPortalMenu menu={menu} />}
		</>
	) : (
		<></>
	);
};

export default MenuExternalPortalMenuPreview;
